import { Injectable } from '@angular/core';
import Dexie from 'dexie';
import { Observable, from, defer } from 'rxjs';

@Injectable({
  providedIn: 'root'
})

/**
 * Cache data in indexDB via Dexie.js. This allows us to load backend data that doesn't
 * change often really quickly.
 */
export class CacheService {

  database: any = null;

  constructor() {

    // Dexie is declarative, this sets up a database the first time its run, if it already exists, it just
    // opens the database.
    this.database = new Dexie('DPWorldDatabase');
    this.database.version(1).stores({
      cache: 'key,value,expires'
    });
  }

  /**
   * Stores data in indexDB.
   * @param key - key name
   * @param value -value of key
   * @param duration - how long this data should be valid for in (minutes)
   * @param storedTime - time in minutes we want the data stored for
   */
  set(key: string, value: any, duration: number): Observable<any> {

    const expires = new Date().getTime() + duration * 60000;

    const promise = this.database.cache.put(
      {
        key,
        value,
        expires
      }
    );

    return from(promise);
  }

  /**
   * Get some data back that was previously stored in indexDB. We return an observable just to keep
   * from mixing promises and observables in the codebase.
   * @param key - name of attribute
   */
  get(key: string): Observable<any> {

    const obs: Observable<any> = defer(async () => {

      // search indexDB for the data
      const promise: Promise<any> = this.database.cache.get(key);
      return await promise.then((data: any) => {
        if (!data) {
          return null;
        }

        // has this data expired?
        if (new Date().getTime() > data.expires) {
          this.delete(key);
          return null;
        } else {
          return data.value;
        }
      });
    });

    return obs;
  }

  /**
   * Used for cleaning up old records.
   * @param key - name of attribute
   */
  delete(key: string) {
    this.database.cache.delete(key);
  }
}
